import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  fetchCalculation,
  updateCalculation,
  deleteCalculation,
  createCalculationItem,
  updateCalculationItem,
  deleteCalculationItem,
  exportCalculation
} from '../../store/calculations/actions';
import CalculationDetailsSection from './components/CalculationDetailsSection';
import CalculationItemSection from './components/CalculationItemSection';
import { useApplyFieldErrors } from '../../hooks/useApplyFieldErrors';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiEdit2, FiPrinter, FiDownload, FiTrash2 } from 'react-icons/fi';

const CalculationDetailsPage = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { 
        current: { 
            data: calculation, 
            loading, 
            error 
        },
        items: {
            items: calculationItems = [],
            loading: itemsLoading = false,
            error: itemsError = null
        }
    } = useSelector(state => state.calculationsData || {
        current: { data: null, loading: false, error: null },
        items: { items: [], loading: false, error: null }
    });

    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        calculationName: { value: '' },
        transportationValues: { value: [0] },
        forwardingAgentValues: { value: [0] },
        items: []
    });

    const fieldErrors = error?.fieldErrors ?? [];
    useApplyFieldErrors(fieldErrors);

    useEffect(() => {
        if (id) {
            dispatch(fetchCalculation(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (calculation) {
            setFormData({
                calculationName: { value: calculation.calculationName || '' },
                transportationValues: { value: calculation.transportationValues || [0] },
                forwardingAgentValues: { value: calculation.forwardingAgentValues || [0] },
                items: calculation.items || []
            });
        }
    }, [calculation]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'transportationValues' || name === 'forwardingAgentValues') {
            setFormData(prev => ({
                ...prev,
                [name]: {
                    ...prev[name],
                    value: [parseFloat(value) || 0]
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: {
                    ...prev[name],
                    value: value
                }
            }));
        }
    };

    const handleSave = async (payload) => {
        try {
            const updateData = {
                calculationName: payload.calculationName,
                transportationValues: payload.transportationValues,
                forwardingAgentValues: payload.forwardingAgentValues
            };
            
            // Check if anything has changed
            const hasChanges = 
                updateData.calculationName.value !== calculation.calculationName ||
                JSON.stringify(updateData.transportationValues.value) !== JSON.stringify(calculation.transportationValues) ||
                JSON.stringify(updateData.forwardingAgentValues.value) !== JSON.stringify(calculation.forwardingAgentValues);
            
            if (!hasChanges) {
                setIsEditing(false);
                toast.info(t('common.noChanges'));
                return;
            }
            
            await dispatch(updateCalculation(id, updateData));
            // Refresh calculation data to show updated values
            dispatch(fetchCalculation(id));
            setIsEditing(false);
        } catch (error) {
            toast.error(error.message || t('calculation.editCalculation') + ' ' + t('common.error'));
        }
    };

    const handleAddItem = async () => {
        // Saga automatically refreshes calculation after adding item
    };

    const handleUpdateItem = async () => {
        // Saga automatically refreshes calculation after updating item
    };

    const handleDeleteItem = async (itemId) => {
        if (window.confirm(t('common.confirmDelete'))) {
            try {
                // Pass calculationId so saga can refresh automatically
                dispatch(deleteCalculationItem(itemId, id));
            } catch (error) {
                toast.error(error.message || t('common.delete') + ' ' + t('common.error'));
            }
        }
    };

    const handleDelete = async () => {
        if (window.confirm(t('common.confirmDelete'))) {
            try {
                await dispatch(deleteCalculation(id));
                navigate('/calculations');
            } catch (error) {
                toast.error(error.message || t('calculation.deleteCalculation') + ' ' + t('common.error'));
            }
        }
    };

    const handleExport = () => {
        if (id) {
            dispatch(exportCalculation(id));
        }
    };
    
    // Handle items errors
    useEffect(() => {
        if (itemsError) {
            toast.error(itemsError.message || t('common.error'));
        }
    }, [itemsError]);

    if (loading && id) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error && !calculation) {
        return (
            <div className="container mx-auto p-6">
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700">
                                {error.message || t('calculation.calculationDetails') + ' ' + t('common.error')}
                            </p>
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/calculations')}
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <FiArrowLeft className="mr-2 h-4 w-4" />
                    {t('common.back')}
                </button>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 lg:p-6">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center">
                    <button
                        onClick={() => navigate('/calculations')}
                        className="mr-4 p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                    >
                        <FiArrowLeft className="h-5 w-5" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {calculation ? `${t('calculation.title')} #${calculation.id}` : t('calculation.createCalculation')}
                    </h1>
                </div>
                
                <div className="mt-4 sm:mt-0 flex space-x-2">
                    {!isEditing && calculation && (
                        <>
                            <button
                                onClick={() => setIsEditing(true)}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <FiEdit2 className="mr-2 h-4 w-4" />
                                {t('common.edit')}
                            </button>
                            <button
                                onClick={handleExport}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <FiDownload className="mr-2 h-4 w-4" />
                                {t('calculation.exportCalculation')}
                            </button>
                            <button
                                onClick={handleDelete}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                <FiTrash2 className="mr-2 h-4 w-4" />
                                {t('common.delete')}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <CalculationDetailsSection 
                calculation={calculation} 
                isEditing={isEditing}
                onSave={handleSave}
                onCancel={() => setIsEditing(false)}
                loading={loading}
            />

            {calculation && (
                <CalculationItemSection
                    items={calculation.items || []}
                    calculationId={id}
                    onAddItem={handleAddItem}
                    onUpdateItem={handleUpdateItem}
                    onDeleteItem={handleDeleteItem}
                    loading={itemsLoading}
                />
            )}
        </div>
    );
};

export default CalculationDetailsPage;
